import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../db/firebase";
import { useNavigate } from "react-router-dom";
import AddSongToEventModal from "./AddSongToEventModal";
import AddSongModal from "./AddSongModal";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import {
  HiOutlinePlus,
  HiOutlineMusicalNote,
  HiOutlinePencilSquare,
  HiOutlineEye,
  HiOutlineTrash,
  HiOutlineStar,
  HiOutlineChevronDown,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
} from "react-icons/hi2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";

const SongArtwork: React.FC<{
  artworkURL?: string | null;
  title: string;
  className?: string;
}> = ({ artworkURL, title, className = "" }) => {
  const [imgError, setImgError] = React.useState(false);
  const showPlaceholder = !artworkURL || imgError;

  if (showPlaceholder) {
    return (
      <div
        className={`flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 shrink-0 ${className}`}
        aria-hidden
      >
        <HiOutlineMusicalNote className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <img
      src={artworkURL}
      alt={title}
      onError={() => setImgError(true)}
      className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover shrink-0 ring-1 ring-black/5 ${className}`}
    />
  );
};

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors";

const glassBtnMuted =
  "inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 transition-colors";

const iconBtn =
  "p-2.5 rounded-xl text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 dark:hover:bg-[#5932EA]/15 transition-colors touch-manipulation";

const ITEMS_PER_PAGE = 12;

const Songs: React.FC = () => {
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const [allSongs, setAllSongs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSong, setSelectedSong] = useState<any | null>(null);
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [songToDelete, setSongToDelete] = useState<any | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const artistDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const loadSongs = useCallback(async () => {
    setLoading(true);
    try {
      const allSongsSnapshot = await getDocs(collection(db, "songs"));
      const songsData = allSongsSnapshot.docs.map((d) => ({
        id: d.id,
        favorite: false,
        ...d.data(),
      }));

      if (organizationId) {
        const favSongsRef = collection(db, "organizations", organizationId, "favorite_songs");
        const favSongsSnapshot = await getDocs(favSongsRef);
        const favSongIds = favSongsSnapshot.docs.map((d) => d.id);
        setAllSongs(
          songsData.map((s) => ({
            ...s,
            favorite: favSongIds.includes(s.id),
          }))
        );
      } else {
        setAllSongs(songsData);
      }
    } catch (error) {
      console.error("Error loading songs:", error);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadSongs();
  }, [loadSongs]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (artistDropdownRef.current && !artistDropdownRef.current.contains(e.target as Node)) {
        setArtistDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allArtists = useMemo(() => {
    const artists = new Set<string>();
    allSongs.forEach((s) => {
      const artist = (s.artist || "").trim();
      if (artist) artists.add(artist);
    });
    return Array.from(artists).sort((a, b) => a.localeCompare(b));
  }, [allSongs]);

  const filteredArtists = useMemo(() => {
    const q = artistSearchQuery.trim().toLowerCase();
    if (!q) return allArtists;
    return allArtists.filter((a) => a.toLowerCase().includes(q));
  }, [allArtists, artistSearchQuery]);

  const displayedSongs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = allSongs;
    if (q) {
      list = list.filter((s) => {
        const title = (s.title || "").toLowerCase();
        const artist = (s.artist || "").toLowerCase();
        return title.includes(q) || artist.includes(q);
      });
    }
    if (selectedArtist) {
      list = list.filter((s: any) => (s.artist || "").trim() === selectedArtist);
    }
    return list;
  }, [allSongs, searchQuery, selectedArtist]);

  const totalPages = Math.max(1, Math.ceil(displayedSongs.length / ITEMS_PER_PAGE));
  const paginatedSongs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return displayedSongs.slice(start, start + ITEMS_PER_PAGE);
  }, [displayedSongs, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedArtist, searchQuery]);

  const confirmDeleteSong = (song: any) => {
    setDeleteError("");
    setSongToDelete(song);
  };

  const handleDelete = async () => {
    if (!songToDelete) return;
    const id = songToDelete.id;
    setDeleteError("");
    try {
      await deleteDoc(doc(db, "songs", id));
      if (organizationId) {
        try {
          await deleteDoc(
            doc(db, "organizations", organizationId, "favorite_songs", id),
          );
        } catch {
          /* favorite entry may not exist */
        }
      }
      setAllSongs((prev) => prev.filter((song) => song.id !== id));
      setSongToDelete(null);
    } catch (error: any) {
      console.error("Error deleting song: ", error);
      setDeleteError(
        (t("errorDeletingSong") || "Could not delete song. ") +
          (error?.message || ""),
      );
      throw error;
    }
  };

  const handleToggleFavorite = async (id: string, currentFavorite: boolean) => {
    if (!organizationId) {
      alert(t("organizationNotFound"));
      return;
    }
    try {
      const favoriteSongDocRef = doc(
        db,
        "organizations",
        organizationId,
        "favorite_songs",
        id,
      );
      if (currentFavorite) {
        await deleteDoc(favoriteSongDocRef);
      } else {
        await setDoc(favoriteSongDocRef, { songId: id });
      }
      setAllSongs((prev) =>
        prev.map((song) =>
          song.id === id ? { ...song, favorite: !currentFavorite } : song,
        ),
      );
    } catch (error) {
      console.error("Error toggling favorite status:", error);
      alert(t("errorTogglingFavorite"));
    }
  };

  const hasFilters = Boolean(searchQuery.trim() || selectedArtist);

  const handleClear = () => {
    setSearchQuery("");
    setSelectedArtist(null);
    setCurrentPage(1);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 sm:space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#5932EA]/10 text-[#5932EA] shrink-0">
            <HiOutlineMusicalNote className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight truncate">
              {t("songs")}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
              {loading
                ? t("loading")
                : `${displayedSongs.length} ${t("songs")?.toLowerCase() || "songs"}`}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowAddSongModal(true)}
          className={`${glassBtn} shrink-0 !px-3 sm:!px-3.5 touch-manipulation`}
        >
          <HiOutlinePlus className="w-5 h-5" />
          <span className="hidden sm:inline">{t("addSong")}</span>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3">
        <div className="relative flex-1">
          <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="search"
            placeholder={t("searchByNameOrArtist")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/70 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] text-base sm:text-sm"
            maxLength={150}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-manipulation"
              aria-label={t("clear")}
            >
              <HiOutlineXMark className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="relative flex gap-2" ref={artistDropdownRef}>
          <button
            type="button"
            onClick={() => setArtistDropdownOpen((o) => !o)}
            className="flex items-center gap-2 flex-1 sm:min-w-[200px] px-4 py-3 rounded-xl text-sm border border-gray-200/80 dark:border-gray-600/80 bg-white/70 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 transition-colors text-left touch-manipulation"
          >
            <span className="truncate flex-1">
              {selectedArtist || t("allArtists") || "All Artists"}
            </span>
            <HiOutlineChevronDown
              className={`w-5 h-5 shrink-0 transition-transform ${artistDropdownOpen ? "rotate-180" : ""}`}
            />
          </button>
          {hasFilters && (
            <button
              type="button"
              onClick={handleClear}
              className={`${glassBtnMuted} shrink-0 touch-manipulation`}
            >
              {t("clear") || "Clear"}
            </button>
          )}
          {artistDropdownOpen && (
            <div className="absolute top-full left-0 right-0 sm:right-auto mt-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg z-50 overflow-hidden min-w-[220px] max-h-72 flex flex-col">
              <div className="p-2 border-b border-gray-100 dark:border-gray-700 shrink-0">
                <input
                  type="text"
                  value={artistSearchQuery}
                  onChange={(e) => setArtistSearchQuery(e.target.value)}
                  placeholder={t("searchArtists") || "Search artists..."}
                  className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
                  autoFocus
                />
              </div>
              <ul className="overflow-y-auto py-1 max-h-56">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedArtist(null);
                      setArtistDropdownOpen(false);
                      setArtistSearchQuery("");
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${!selectedArtist ? "bg-[#5932EA]/10 text-[#5932EA] font-medium" : "text-gray-700 dark:text-gray-300"}`}
                  >
                    {t("allArtists") || "All Artists"}
                  </button>
                </li>
                {filteredArtists.map((artist) => (
                  <li key={artist}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedArtist(artist);
                        setArtistDropdownOpen(false);
                        setArtistSearchQuery("");
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 truncate ${selectedArtist === artist ? "bg-[#5932EA]/10 text-[#5932EA] font-medium" : "text-gray-700 dark:text-gray-300"}`}
                    >
                      {artist}
                    </button>
                  </li>
                ))}
                {filteredArtists.length === 0 && (
                  <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {t("noArtistsFound") || "No artists found"}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {deleteError && (
        <div className="px-4 py-3 rounded-xl border border-red-200/70 dark:border-red-800/50 bg-red-50/80 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
          {deleteError}
        </div>
      )}

      <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden">
        {loading ? (
          <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
            {Array.from({ length: 5 }).map((_, index) => (
              <li key={index} className="px-4 py-3.5">
                <Skeleton height={48} />
              </li>
            ))}
          </ul>
        ) : displayedSongs.length > 0 ? (
          <>
            <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
              {paginatedSongs.map((song) => (
                <li
                  key={song.id}
                  className="flex items-center gap-2.5 sm:gap-3 px-3.5 sm:px-4 py-3 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/songs/${song.id}`)}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left touch-manipulation"
                  >
                    <SongArtwork
                      artworkURL={song.artworkURL}
                      title={song.title}
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                        {song.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {song.artist || "—"}
                        {song.album ? (
                          <span className="hidden sm:inline">
                            <span className="text-gray-300 dark:text-gray-600 mx-1.5" aria-hidden>
                              ·
                            </span>
                            {song.album}
                          </span>
                        ) : null}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setSelectedSong(song)}
                      className={`${iconBtn} text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10`}
                      title={t("addToEvent") || "Add to event"}
                      aria-label={t("addToEvent") || "Add to event"}
                    >
                      <HiOutlinePlus className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/songs/${song.id}/edit`)}
                      className={iconBtn}
                      title={t("edit")}
                      aria-label={t("edit")}
                    >
                      <HiOutlinePencilSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/songs/${song.id}`)}
                      className={`${iconBtn} hidden sm:inline-flex`}
                      title={t("view")}
                      aria-label={t("view")}
                    >
                      <HiOutlineEye className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmDeleteSong(song)}
                      className="p-2.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors touch-manipulation"
                      title={t("delete")}
                      aria-label={t("delete")}
                    >
                      <HiOutlineTrash className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleToggleFavorite(song.id, song.favorite)
                      }
                      className={`p-2.5 rounded-xl transition-colors touch-manipulation ${
                        song.favorite
                          ? "text-amber-500 hover:bg-amber-500/10"
                          : "text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10"
                      }`}
                      title={
                        song.favorite
                          ? t("removeFromFavorites")
                          : t("addToFavorites")
                      }
                      aria-label={
                        song.favorite
                          ? t("removeFromFavorites")
                          : t("addToFavorites")
                      }
                    >
                      <HiOutlineStar
                        className={`w-4 h-4 sm:w-5 sm:h-5 ${song.favorite ? "fill-current" : ""}`}
                      />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            {totalPages > 1 && (
              <div className="flex justify-between items-center gap-2 px-3.5 sm:px-4 py-3 border-t border-gray-100/80 dark:border-gray-700/80">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`${glassBtnMuted} disabled:opacity-40 !px-3 touch-manipulation`}
                >
                  {t("previous") || "Previous"}
                </button>
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`${glassBtnMuted} disabled:opacity-40 !px-3 touch-manipulation`}
                >
                  {t("next") || "Next"}
                </button>
              </div>
            )}
          </>
        ) : allSongs.length === 0 ? (
          <button
            type="button"
            onClick={() => setShowAddSongModal(true)}
            className="w-full flex flex-col items-center justify-center py-14 text-gray-500 dark:text-gray-400 hover:bg-[#5932EA]/5 hover:text-[#5932EA] transition-all touch-manipulation"
          >
            <div className="w-14 h-14 mb-3 rounded-2xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center">
              <HiOutlineMusicalNote className="w-7 h-7" />
            </div>
            <p className="text-sm font-medium">{t("noSongsFound")}</p>
            <p className="text-xs mt-1">{t("addSong")}</p>
          </button>
        ) : (
          <div className="flex flex-col items-center justify-center py-14 text-gray-500 dark:text-gray-400">
            <HiOutlineMusicalNote className="w-12 h-12 mb-2 opacity-40" />
            <p className="text-sm font-medium">{t("noSongsFound")}</p>
            <p className="text-xs mt-1">
              {t("noSongsMatchFilters") || "No songs match your filters."}
            </p>
          </div>
        )}
      </section>

      {selectedSong && (
        <AddSongToEventModal
          song={selectedSong}
          onClose={() => setSelectedSong(null)}
        />
      )}
      {showAddSongModal && (
        <AddSongModal
          onClose={() => setShowAddSongModal(false)}
          onSongAdded={loadSongs}
        />
      )}
      {songToDelete && (
        <ConfirmDeleteModal
          isOpen
          onClose={() => {
            setSongToDelete(null);
            setDeleteError("");
          }}
          onConfirm={handleDelete}
          userName={songToDelete.title || t("untitledSong") || "this song"}
        />
      )}
    </div>
  );
};

export default Songs;
